import React from 'react';

import {
  TextInput,
  Button,
  ActionIcon,
  Space,
  Title,
  createStyles,
  Paper,
  Checkbox,
  Text,
  Tooltip,
} from '@mantine/core';
import { IconX, IconSearch, IconPlus, IconFilterOff } from '@tabler/icons-react';
import ShoppingCartWithBadge from 'components/ShoppingCartWithBadge';

const useStyles = createStyles(theme => ({
  header: {
    padding: theme.spacing.lg,
    background: theme.colors.gray[0],
    borderRadius: 0,
    borderBottom: `1px solid ${theme.colors.gray[3]}`,
    display: 'grid',
    gridTemplateRows: 'auto',
    gridTemplateColumns: 'auto min-content min-content',
    gridTemplateAreas: `
      'title button button'
      'search search search'
      'favorite . clear'
    `,
    alignItems: 'center',
    gap: theme.spacing.sm,
    [theme.fn.smallerThan('lg')]: {
      gridTemplateColumns: 'auto 10px min-content',
    },
  },
  title: {
    gridArea: 'title',
  },
  favorite: {
    gridArea: 'favorite',
    whiteSpace: 'nowrap',
  },
  search: {
    gridArea: 'search',
  },
  clear: {
    gridArea: 'clear',
  },
  button: {
    gridArea: 'button',
  },
}));

const FeesAndMiscProductsPageHeader = ({
  isDesktop,
  count,
  search,
  setSearch,
  orderByFavorites,
  setOrderByFavorites,
  openDrawer,
  openCart,
  productMutatePerm = true,
}: {
  isDesktop: boolean;
  count: number;
  search: string;
  setSearch: React.Dispatch<React.SetStateAction<string>>;
  orderByFavorites: boolean;
  setOrderByFavorites: React.Dispatch<React.SetStateAction<boolean>>;
  openDrawer: () => void;
  openCart: () => void;
  productMutatePerm?: boolean;
}): JSX.Element => {
  const { classes, theme } = useStyles();

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const value = e.currentTarget?.value || '';
    setSearch(value);
  };

  const handleClearSearchFilter = (): void => {
    setSearch('');
  };

  const handleOrderByFavorites = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setOrderByFavorites(!!e.currentTarget?.checked);
  };

  const handleClearFilters = (): void => {
    setSearch('');
    setOrderByFavorites(false);
  };

  return (
    <Paper className={classes.header}>
      <Title order={1} size='h1' pr='xl' className={classes.title}>
        Fees and Miscellaneous
      </Title>
      <Checkbox
        className={classes.favorite}
        checked={orderByFavorites}
        onChange={handleOrderByFavorites}
        label={
          <Text color='gray' fs='italic'>
            Order by Favorites
          </Text>
        }
      />
      <TextInput
        className={classes.search}
        icon={<IconSearch size={theme.fontSizes.md} />}
        placeholder='Search by product'
        onChange={handleSearchChange}
        value={search}
        rightSection={
          !!search ? (
            <ActionIcon onClick={handleClearSearchFilter} size='md'>
              <IconX size={theme.fontSizes.md} />
            </ActionIcon>
          ) : (
            <Space />
          )
        }
      />
      <Tooltip label='Clear filters' className={classes.clear}>
        <ActionIcon onClick={handleClearFilters}>
          <IconFilterOff />
        </ActionIcon>
      </Tooltip>

      {isDesktop ? (
        <Button
          className={classes.button}
          leftIcon={<IconPlus size={theme.fontSizes.md} />}
          onClick={openDrawer}
          disabled={!productMutatePerm}
        >
          Product
        </Button>
      ) : (
        <ShoppingCartWithBadge className={classes.button} onClick={openCart} count={count} />
      )}
    </Paper>
  );
};

export default FeesAndMiscProductsPageHeader;
